// Direct React import — using a re-export shim was creating two distinct
// React instances, which broke React Context propagation between providers
// (rendered via shim) and consumers (rendered via direct 'react' imports).
// Symptom: useAuth() returned the default empty context in ChatbotList
// even though AuthProvider had populated it. Fix: import 'react' directly
// everywhere so all components share the same internal context registry.
import * as React from "react";
import { useState, useEffect, lazy, Suspense } from "react";
// Import other dependencies
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import { QueryClient } from "@tanstack/react-query";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import AuthMiddleware from "@/middleware/AuthMiddleware";
import { VersionDisplay } from "@/components/ui/VersionDisplay";
// REMOVED: import { supabase } from "@/integrations/supabase/client"
// This dead import was causing the GoTrueClient to initialize on every
// page load, which hangs on the production domain. App.tsx never uses
// the supabase client directly — auth is handled by AuthContext.

// Import pages
// NOTE: lazy() was attempted for heavy portal pages but Vite warned the
// imports couldn't be split because shared hooks (useBuilderSave,
// useChatbot, embeddedRoutes) already statically import the same modules.
// Un-tangling that chain is a separate refactor — for now, everything
// stays eager. The AuthMiddleware + ChatbotList perf wins still apply.
import Index from "@/pages/Index";
import Portal from "@/pages/portal/Portal";
// These pages import the supabase client for auth calls (signIn/signUp).
// Lazy-loading them prevents the GoTrueClient from initializing until
// the user actually navigates to login/signup — which avoids the
// navigator.locks hang on the production domain.
const PortalAuth = lazy(() => import("@/pages/portal/PortalAuth"));
const PortalSignup = lazy(() => import("@/pages/portal/PortalSignup"));
const StreamlinedSignup = lazy(() => import("@/pages/portal/StreamlinedSignup"));
import PortalChatbots from "@/pages/portal/PortalChatbots";
import PortalBilling from "@/pages/portal/PortalBilling";
import PortalBillingPlan from "@/pages/portal/PortalBillingPlan";
const PortalProfile = lazy(() => import("@/pages/portal/PortalProfile"));
import PortalSettings from "@/pages/portal/PortalSettings";
const PortalBuilderWithContext = lazy(() => import("@/pages/portal/PortalBuilderWithContext"));
import PortalFlowBuilder from "@/pages/portal/PortalFlowBuilder";
import PortalAnalytics from "@/pages/portal/PortalAnalytics";
import PortalConversations from "@/pages/portal/PortalConversations";
import PortalConversationDetail from "@/pages/portal/PortalConversationDetail";
import PortalVerticals from "@/pages/portal/PortalVerticals";
import PortalScoringConfig from "@/pages/portal/PortalScoringConfig";
const PortalScoringRules = lazy(() => import("@/pages/portal/PortalScoringRules"));
const PortalDecisionRules = lazy(() => import("@/pages/portal/PortalDecisionRules"));
const PortalOutcomes = lazy(() => import("@/pages/portal/PortalOutcomes"));
const PortalLearning = lazy(() => import("@/pages/portal/PortalLearning"));
const PortalSecurity = lazy(() => import("@/pages/portal/PortalSecurity"));
const PortalAffiliates = lazy(() => import("@/pages/portal/PortalAffiliates"));
const PortalBuyers = lazy(() => import("@/pages/portal/PortalBuyers"));
const PortalAttribution = lazy(() => import("@/pages/portal/PortalAttribution"));
const PortalWhiteLabel = lazy(() => import("@/pages/portal/PortalWhiteLabel"));
const PortalLandingPages = lazy(() => import("@/pages/portal/PortalLandingPages"));
const PortalFraudDetection = lazy(() => import("@/pages/portal/PortalFraudDetection"));
const PortalOffers = lazy(() => import("@/pages/portal/PortalOffers"));
const PortalGlobalPostbacks = lazy(() => import("@/pages/portal/PortalGlobalPostbacks"));
const PortalSmartLinks = lazy(() => import("@/pages/portal/PortalSmartLinks"));
const PortalPixels = lazy(() => import("@/pages/portal/PortalPixels"));
const PortalDocs = lazy(() => import("@/pages/portal/PortalDocs"));
const PortalCallBuyers = lazy(() => import("@/pages/portal/PortalCallBuyers"));
const PortalRPUS = lazy(() => import("@/pages/portal/PortalRPUS"));
const PortalSMS = lazy(() => import("@/pages/portal/PortalSMS"));
import PortalVerticalPerformance from "@/pages/portal/PortalVerticalPerformance";
import PortalSessionReplay from "@/pages/portal/PortalSessionReplay";
import PortalEmbed from "@/pages/portal/PortalEmbed";
import PortalWebhookTester from "@/pages/portal/PortalWebhookTester";
import PortalLeadExport from "@/pages/portal/PortalLeadExport";
import PortalABTesting from "@/pages/portal/PortalABTesting";
import PortalNotifications from "@/pages/portal/PortalNotifications";
import PortalAuditLog from "@/pages/portal/PortalAuditLog";
import PortalTeam from "@/pages/portal/PortalTeam";
import PortalAPIKeys from "@/pages/portal/PortalAPIKeys";
import PortalIntegrations from "@/pages/portal/PortalIntegrations";
import PortalLN360Test from "@/pages/portal/PortalLN360Test";
const FullscreenChat = lazy(() => import("@/pages/FullscreenChat"));
const UnifiedChatPage = lazy(() => import("@/pages/UnifiedChatPage"));
import TryPage from "@/pages/TryPage";
import Compare from "@/pages/Compare";
import NotFound from "@/pages/NotFound";
const BillingPage = lazy(() => import("@/pages/settings/BillingPage"));

// Import Admin components
import PortalLayout from '@/components/portal/PortalLayout';
import AdminPageLoader from '@/components/admin/AdminPageLoader';

// Import docs routes
import { docsRoutes } from '@/routes/docsRoutes';
// Import embedded routes
import { embeddedRoutes } from '@/routes/embeddedRoutes';

const queryClient = new QueryClient();

const LazyStandaloneCampaign = React.lazy(() => import('@/pages/campaign/StandaloneCampaign'));
const LazyPortalCampaigns = React.lazy(() => import('@/pages/portal/PortalCampaigns'));
const LazyUserManagementPage = React.lazy(() => import('@/pages/admin/UserManagementPage'));
const LazyUserDetailsPage = React.lazy(() => import('@/pages/admin/UserDetailsPage'));
const LazyUserEditPage = React.lazy(() => import('@/pages/admin/UserEditPage'));
const LazyUserCreatePage = React.lazy(() => import('@/pages/admin/UserCreatePage'));
const LazyGlobalAISettingsPage = React.lazy(() => import('@/pages/admin/GlobalAISettingsPage'));
const LazyPlanManagementPage = React.lazy(() => import('@/pages/admin/PlanManagementPage'));
const LazyFieldsDictionaryPage = React.lazy(() => import('@/pages/admin/FieldsDictionaryPage'));
const LazyTermsOfService = React.lazy(() => import('@/pages/legal/TermsOfService'));
const LazyPrivacyPolicy = React.lazy(() => import('@/pages/legal/PrivacyPolicy'));

// Wrapper component to connect AuthMiddleware with AuthContext
function AuthWrapper() {
  const { updateAuthState } = useAuth();

  return (
    <AuthMiddleware onAuthStateChange={updateAuthState}>
      <div className="routes-container">
        <AppRoutes />
        <Toaster />
        <VersionDisplay position="bottom-right" detailed={true} />
      </div>
    </AuthMiddleware>
  );
}

function AppRoutes() {
  const { session, isLoading, isAdmin } = useAuth();

  // AuthMiddleware handles all authentication - remove session-based redirects

  return (
    <Suspense fallback={<div className="min-h-screen bg-slate-950 flex items-center justify-center"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>}>
    <Routes>
      <Route path="/" element={<Index />} />
      <Route path="/compare" element={<Compare />} />
      <Route path="/try" element={<TryPage />} />
      <Route path="/portal" element={<Portal />} />
      <Route path="/portal/dashboard" element={<Navigate to="/portal" replace />} />
      <Route path="/portal/chatbots" element={<PortalChatbots />} />
      <Route path="/portal/campaigns" element={<Suspense fallback={<AdminPageLoader />}><LazyPortalCampaigns /></Suspense>} />
      <Route path="/c/:slug" element={<Suspense fallback={<AdminPageLoader />}><LazyStandaloneCampaign /></Suspense>} />
      <Route path="/portal/billing" element={<Navigate to="/portal/billing/plan" />} />
      <Route path="/portal/billing/plan" element={<PortalBillingPlan />} />
      <Route path="/portal/billing/payment-methods" element={<PortalBilling />} />
      <Route path="/settings/billing" element={<BillingPage />} />
      <Route path="/portal/profile" element={<PortalProfile />} />
      <Route path="/portal/settings" element={<PortalSettings />} />
      <Route path="/portal/builder/:id?" element={<PortalBuilderWithContext />} />
      <Route path="/portal/flow/:id" element={<PortalFlowBuilder />} />
      <Route path="/portal/verticals" element={<PortalVerticals />} />
      <Route path="/portal/scoring" element={<PortalScoringConfig />} />
      <Route path="/portal/scoring-rules" element={<Suspense fallback={<AdminPageLoader />}><PortalScoringRules /></Suspense>} />
      <Route path="/portal/decision-rules" element={<Suspense fallback={<AdminPageLoader />}><PortalDecisionRules /></Suspense>} />
      <Route path="/portal/outcomes" element={<Suspense fallback={<AdminPageLoader />}><PortalOutcomes /></Suspense>} />
      <Route path="/portal/learning" element={<Suspense fallback={<AdminPageLoader />}><PortalLearning /></Suspense>} />
      <Route path="/portal/security" element={<Suspense fallback={<AdminPageLoader />}><PortalSecurity /></Suspense>} />
      <Route path="/portal/affiliates" element={<Suspense fallback={<AdminPageLoader />}><PortalAffiliates /></Suspense>} />
      <Route path="/portal/buyers" element={<Suspense fallback={<AdminPageLoader />}><PortalBuyers /></Suspense>} />
      <Route path="/portal/attribution" element={<Suspense fallback={<AdminPageLoader />}><PortalAttribution /></Suspense>} />
      <Route path="/portal/whitelabel" element={<Suspense fallback={<AdminPageLoader />}><PortalWhiteLabel /></Suspense>} />
      <Route path="/portal/pages" element={<Suspense fallback={<AdminPageLoader />}><PortalLandingPages /></Suspense>} />
      <Route path="/portal/fraud" element={<Suspense fallback={<AdminPageLoader />}><PortalFraudDetection /></Suspense>} />
      <Route path="/portal/offers" element={<Suspense fallback={<AdminPageLoader />}><PortalOffers /></Suspense>} />
      <Route path="/portal/postbacks" element={<Suspense fallback={<AdminPageLoader />}><PortalGlobalPostbacks /></Suspense>} />
      <Route path="/portal/smart-links" element={<Suspense fallback={<AdminPageLoader />}><PortalSmartLinks /></Suspense>} />
      <Route path="/portal/pixels" element={<Suspense fallback={<AdminPageLoader />}><PortalPixels /></Suspense>} />
      <Route path="/portal/docs" element={<Suspense fallback={<AdminPageLoader />}><PortalDocs /></Suspense>} />
      <Route path="/portal/call-buyers" element={<Suspense fallback={<AdminPageLoader />}><PortalCallBuyers /></Suspense>} />
      <Route path="/portal/rpus" element={<Suspense fallback={<AdminPageLoader />}><PortalRPUS /></Suspense>} />
      <Route path="/portal/sms" element={<Suspense fallback={<AdminPageLoader />}><PortalSMS /></Suspense>} />
      <Route path="/portal/embed" element={<PortalEmbed />} />
      <Route path="/portal/performance" element={<PortalVerticalPerformance />} />
      <Route path="/portal/sessions" element={<PortalSessionReplay />} />
      <Route path="/portal/webhooks" element={<PortalWebhookTester />} />
      <Route path="/portal/exports" element={<PortalLeadExport />} />
      <Route path="/portal/ab-tests" element={<PortalABTesting />} />
      <Route path="/portal/notifications" element={<PortalNotifications />} />
      <Route path="/portal/audit" element={<PortalAuditLog />} />
      <Route path="/portal/team" element={<PortalTeam />} />
      <Route path="/portal/api-keys" element={<PortalAPIKeys />} />
      <Route path="/portal/integrations" element={<PortalIntegrations />} />
      <Route path="/portal/ln360" element={<PortalLN360Test />} />
      <Route path="/portal/analytics" element={<PortalAnalytics />} />
      <Route path="/portal/analytics/:id" element={<PortalAnalytics />} />
      <Route path="/portal/conversations/:chatbotId" element={<PortalConversations />} />
      <Route path="/portal/conversation/:conversationId" element={<PortalConversationDetail />} />
      <Route path="/portal/conversation/:conversationId/:chatbotId" element={<PortalConversationDetail />} />

      <Route path="/chat/:chatId" element={<UnifiedChatPage />} />
      {/* Keep legacy route for backward compatibility */}
      <Route path="/fullscreen-chat/:chatId" element={<FullscreenChat />} />
      <Route path="/portal/auth" element={<PortalAuth />} />
      <Route path="/portal/signup" element={<PortalSignup />} />
      <Route path="/portal/signup-streamlined" element={<StreamlinedSignup />} />

      {/* === Embedded Chat Routes === */}
      {embeddedRoutes}

      {/* === Documentation Routes === */}
      {docsRoutes}

      {/* === Admin Routes (Integrated into Portal) === */}
      {/* Redirect /admin to /admin/users */}
      <Route path="/admin" element={<Navigate to="/admin/users" replace />} />
      <Route
        path="/admin/users"
        element={<Suspense fallback={<AdminPageLoader />}><LazyUserManagementPage /></Suspense>}
      />
      <Route
        path="/admin/users/:userId"
        element={<Suspense fallback={<AdminPageLoader />}><LazyUserDetailsPage /></Suspense>}
      />
      <Route
        path="/admin/users/:userId/edit"
        element={<Suspense fallback={<AdminPageLoader />}><LazyUserEditPage /></Suspense>}
      />
      <Route
        path="/admin/users/new"
        element={<Suspense fallback={<AdminPageLoader />}><LazyUserCreatePage /></Suspense>}
      />
      <Route
        path="/admin/ai-settings"
        element={<Suspense fallback={<AdminPageLoader />}><LazyGlobalAISettingsPage /></Suspense>}
      />
      <Route
        path="/admin/plans"
        element={<Suspense fallback={<AdminPageLoader />}><LazyPlanManagementPage /></Suspense>}
      />
      <Route
        path="/admin/fields-dictionary"
        element={<Suspense fallback={<AdminPageLoader />}><LazyFieldsDictionaryPage /></Suspense>}
      />

      {/* === Legal Pages === */}
      <Route
        path="/terms"
        element={<Suspense fallback={<div className="min-h-screen bg-slate-950" />}><LazyTermsOfService /></Suspense>}
      />
      <Route
        path="/privacy"
        element={<Suspense fallback={<div className="min-h-screen bg-slate-950" />}><LazyPrivacyPolicy /></Suspense>}
      />

      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  // Add a state to track React initialization status
  const [reactInitialized, setReactInitialized] = useState<boolean>(false);

  // Check if React is properly initialized
  useEffect(() => {
    if (React && React.forwardRef) {
      console.log('React is properly initialized:', React.version);
      setReactInitialized(true);
    } else {
      console.error('React forwardRef is not available!');
    }
  }, []);

  if (!reactInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Initializing...</p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="app-container">
        <QueryClientProvider client={queryClient}>
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <TooltipProvider>
              <AuthProvider>
                <AuthWrapper />
              </AuthProvider>
            </TooltipProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </div>
    </ErrorBoundary>
  );
}

export default App;
